import type { FC, ReactNode } from "preact/compat";
import { useEffect, useState } from "preact/hooks";
import { createBrowserHistory } from "history";
import { navigate } from "astro:transitions/client";
import qs from "query-string";
import { useIsMobile } from "@hooks/useIsMobile";
import { ComboboxModalProvider } from "@components/ComboBoxes/ComboboxModalProvider";
import type {
  Payor,
  Payors,
  SelectedItem,
  Specialties,
  State,
  States,
} from "@services/filter-api/types";
import type { GravityForm } from "@services/wordpress/wordpress.types";

export interface FiltersChildrenProps {
  handleRedirectClick?: (event: Event) => void;
  getRedirectUrl?: () => string;
  requiredFiltersAllValid?: () => boolean;
  selectedItems: {
    state: State | null;
    insurance: Payor | null;
    specialty: Specialties | null;
  };
  selectLater?: {
    value: string;
    label: string;
    selected?: boolean | undefined;
  };
  handleSelectLater?: (selected: boolean) => void;
  isMobile?: boolean;
  disabled_features?: Array<{ value: string }>;
  insurance: Payors;
  statesFilters: States;
  specialty: Specialties;
  handleOpenStates: (open: boolean) => void;
  handleGetSelectedInsurance: (item: SelectedItem) => void;
  handleGetSelectedState: (item: SelectedItem) => void;
  handleOpenNeeds: (open: boolean) => void;
  handleOpenInsurance: (open: boolean) => void;
  handleGetSelectedNeeds: (
    items: {
      value: string;
      label: string;
      selected: boolean;
    }[],
  ) => void;
  openNeeds: boolean;
  openStates: boolean;
  openInsurance: boolean;
  errors?: {
    state: string;
    insurance: string;
  };
  type_of_care?: string;
}

export interface FiltersProps {
  children?: (props: FiltersChildrenProps) => ReactNode;
  email_capture_form?: GravityForm;
  states: States;
  specialties: Specialties;
  disabled_features?: Array<{ value: string }>;
  optional_filter_props?: Array<"state" | "needs" | "insurance">;
  insurance: Payors;
  filter_buttons_props?: Array<{
    name: string;
    label: string;
    is_optional: boolean;
    is_multi_select: boolean;
  }>;
  type_of_care?: string;
  onRedirectUrlChange?: (url: string) => void;
  preSelectedLocation?: string;
  preSelectedInsurance?: string;
  preSelectedSpecialties?: string;
}

export const Filters: FC<FiltersProps> = ({
  specialties: specialty,
  states: statesFilters,
  insurance,
  disabled_features,
  optional_filter_props,
  filter_buttons_props,
  children,
  type_of_care,
  onRedirectUrlChange,
  preSelectedLocation,
  preSelectedInsurance,
  preSelectedSpecialties,
}) => {
  const isMobile = useIsMobile();
  const [openNeeds, setOpenNeeds] = useState(false);
  const [openStates, setOpenStates] = useState(false);
  const [openInsurance, setOpenInsurance] = useState(false);
  const [nonFilterQquery, setNonFilterQuery] = useState<string>("");
  const [BaseRedirectUrl, setBaseRedirectUrl] = useState<string>("");
  const [errors, setErrors] = useState<{
    state: string;
    insurance: string;
  }>({
    state: "",
    insurance: "",
  });
  const [selectLater, setSelectLater] = useState<{
    value: string;
    label: string;
    selected?: boolean;
  }>({
    value: "select_later",
    label: "I'll select this later",
    selected: false,
  });
  const [selectedItems, setSelectedItems] = useState<{
    state: State | null;
    insurance: Payor | null;
    specialty: Specialties | null;
  }>({
    state: null,
    insurance: null,
    specialty: null,
  });
  const [effectiveTypeOfCare, setEffectiveTypeOfCare] = useState<string | undefined>(type_of_care);

  const handleGetSelectedInsurance = (item: SelectedItem) => {
    if (item.selected) {
      setSelectedItems({
        ...selectedItems,
        insurance: item,
      });
      setErrors({
        ...errors,
        insurance: "",
      });
    } else {
      setSelectedItems({
        ...selectedItems,
        insurance: null,
      });
    }
  };

  const handleGetSelectedState = (item: SelectedItem) => {
    if (item.selected) {
      setSelectedItems({
        ...selectedItems,
        state: item,
      });
      setErrors({
        ...errors,
        state: "",
      });
    } else {
      setSelectedItems({
        ...selectedItems,
        state: null,
      });
    }
  };

  const handleGetSelectedNeeds = (
    items: { value: string; label: string; selected: boolean }[],
  ) => {
    if (items.length) {
      setSelectedItems({
        ...selectedItems,
        specialty: items,
      });
      setSelectLater({
        ...selectLater,
        selected: false,
      });
    } else {
      setSelectedItems({
        ...selectedItems,
        specialty: null,
      });
      setSelectLater({
        ...selectLater,
        selected: true,
      });
    }
  };

  const handleOpenNeeds = (open: boolean) => {
    setOpenNeeds(open);
  };
  const handleOpenInsurance = (open: boolean) => {
    setOpenInsurance(open);
  };
  const handleOpenStates = (open: boolean) => {
    setOpenStates(open);
  };

  const handleSelectLater = (selected: boolean) => {
    setSelectLater({
      ...selectLater,
      selected,
    });
    setSelectedItems({
      ...selectedItems,
      specialty: [],
    });
  };

  const setQuery = (items: {
    [filter: string]:
      | {
          label: string;
          value: string;
        }
      | {
          label: string;
          value: string;
        }[]
      | null;
    state: State | null;
    insurance: Payor | null;
    specialty: Specialties | null;
  }) => {
    let query = "";
    const itemsKey = Object.keys(items).filter((_item: string) => items[_item]);
    if (itemsKey.length) {
      itemsKey.forEach((_item, index) => {
        if (index === 0) {
          query = `?`;
        } else if (index > 0) {
          query = `${query}&`;
        }

        if (_item === "specialty" && !selectLater.selected) {
          query = `${query}${items[_item]
            ?.map((_item) => _item.label)
            ?.reduce((prev, curr, index) => {
              if (index === 0) {
                return `${prev}specialty[${index}]=${curr}`;
              }
              return `${prev}&specialty[${index}]=${curr}`;
            }, "")}`;
        }
        if ((_item === "state" || _item === "insurance") && items[_item]) {
          query = `${query}${_item}=${items[_item]?.label}`;
        }
      });
    }

    return query;
  };

  const setDisabled = () => {
    const props = {} as {
      [key: string]: boolean;
    };
    const redirectErrors = {} as {
      state: string;
      insurance: string;
      specialty: string;
    };
    const insurance =
      selectedItems?.insurance && Object.values(selectedItems?.insurance).length
        ? true
        : false;
    const state =
      selectedItems?.state && Object.values(selectedItems?.state).length
        ? true
        : false;

    if (
      filter_buttons_props?.find((_prop) => _prop.name === "insurance")
        ?.is_optional === false
    ) {
      props["insurance"] = insurance;
      const error = insurance ? "" : "Please select an insurance";
      redirectErrors["insurance"] = error;
    }
    if (
      filter_buttons_props?.find((_prop) => _prop.name === "state")
        ?.is_optional === false
    ) {
      props["state"] = state;
      const error = state ? "" : "Please select a state";
      redirectErrors["state"] = error;
    }

    // Only update errors if they've actually changed
    const hasErrorChanges = 
      redirectErrors.insurance !== errors.insurance || 
      redirectErrors.state !== errors.state;
    
    if (hasErrorChanges) {
      setErrors(redirectErrors);
    }

    if (!Object.values(props).length) {
      return true;
    }

    if (Object.values(props).every((_prop) => _prop)) {
      // Only clear errors if they're not already empty
      if (errors.insurance !== "" || errors.state !== "") {
        setErrors({
          insurance: "",
          state: "",
        });
      }
      return false;
    } else {
      return true;
    }
  };

  const findParsedState = (value: string) => {
    const key = value.includes(',')
      ? value.split(',')[0]
      : value.toLowerCase();

    return statesFilters.find((_state) =>
      value.includes(',')
        ? _state.abbreviation === key
        : _state.label.toLowerCase() === key
    );
  };

  useEffect(() => {
    const parsedQuery = window.location.search ? qs.parse(window.location.search) : {};

    // Extract non-filter parameters for query generation
    const nonFilterParams = Object.keys(parsedQuery).filter((_q) => {
      return !Object.keys(selectedItems).some((_item) => _q.includes(_item));
    });

    const generateNonFilterQuery = nonFilterParams
      .filter((_p) => parsedQuery[_p])
      .map((_p) => `${_p}=${parsedQuery[_p]}`)
      .join("&");

    // Parse URL parameters (take priority)
    const parsedInsurance = parsedQuery.insurance ? 
      insurance.find((_insurer) =>
        _insurer.label.toLowerCase() === parsedQuery.insurance?.toString().toLowerCase()
      ) : null;

    const { address, state } = parsedQuery;
    const parsedState = typeof address === 'string' ? findParsedState(address) :
                       typeof state === 'string' ? findParsedState(state) : null;

    // Parse specialty parameters from URL more efficiently
    const urlSpecialties = Object.keys(parsedQuery)
      .filter((_param) => _param.includes("specialty"))
      .map((_param) => parsedQuery[_param] as string);

    const parsedNeeds = urlSpecialties.length > 0 ? 
      specialty.filter((_need) =>
        urlSpecialties.some((_n) => _n.toLowerCase() === _need.label.toLowerCase())
      ) : [];

    // Apply pre-selected values as fallback when URL parameters are not present
    const preSelectedState = preSelectedLocation && !parsedState ? 
      statesFilters.find(state => state.value?.toLowerCase() === preSelectedLocation.toLowerCase()) : null;

    const preSelectedInsuranceValue = preSelectedInsurance && !parsedInsurance ? 
      insurance.find(ins => ins.value === preSelectedInsurance) : null;

    const preSelectedSpecialtiesValue = preSelectedSpecialties && !parsedNeeds.length ? 
      specialty
        .filter(spec => preSelectedSpecialties.split(',').includes(spec.value))
        .map(spec => ({ ...spec, selected: true })) : null;

    // Build selected items with proper precedence (URL params > pre-selected values)
    const newSelectedItems = {
      ...selectedItems,
      ...(parsedInsurance && { insurance: parsedInsurance }),
      ...(parsedState && { state: parsedState }),
      ...(parsedNeeds.length > 0 && { specialty: parsedNeeds }),
      // Apply pre-selected values only if URL parameters are not present
      ...(preSelectedInsuranceValue && { insurance: preSelectedInsuranceValue }),
      ...(preSelectedState && { state: preSelectedState }),
      ...(preSelectedSpecialtiesValue && preSelectedSpecialtiesValue.length > 0 && { specialty: preSelectedSpecialtiesValue }),
    };

    setSelectedItems(newSelectedItems);

    // Update selectLater state if specialties are selected
    const hasSpecialties = parsedNeeds.length > 0 || (preSelectedSpecialtiesValue && preSelectedSpecialtiesValue.length > 0);
    if (hasSpecialties) {
      setSelectLater(prev => ({ ...prev, selected: false }));
    }

    setNonFilterQuery(`&${generateNonFilterQuery}`);

    // Update type of care if present in URL
    if (parsedQuery.typeOfCare) {
      setEffectiveTypeOfCare(parsedQuery.typeOfCare as string);
    }
  }, [preSelectedLocation, preSelectedInsurance, preSelectedSpecialties, statesFilters, insurance, specialty]);

  // Add event listener for insurance logo selection from Filter Modal trigger
  useEffect(() => {
    const handleInsuranceLogoSelection = (event: CustomEvent) => {
      const insuranceValue = event.detail.value;
      const selectedInsurance = insurance.find(
        (_insurer) => _insurer.value.toLowerCase() === insuranceValue.toLowerCase()
      );
      
      if (selectedInsurance) {
        setSelectedItems({
          ...selectedItems,
          insurance: selectedInsurance
        });
        setErrors({
          ...errors,
          insurance: ""
        });
      }
    };

    document.addEventListener('insurance-logo-selected', handleInsuranceLogoSelection as EventListener);
    
    return () => {
      document.removeEventListener('insurance-logo-selected', handleInsuranceLogoSelection as EventListener);
    };
  }, [insurance, selectedItems, errors]);

  useEffect(() => {
    const redirectState = statesFilters.find(
      (_state) => _state.label === selectedItems.state?.label || "",
    );

    let updatedUrl = `{origin}/find/${redirectState?.slug}/${selectedItems.insurance?.slug}`;

    // Override settings and address parameters in nonFilterQquery if they exist
    const urlSearchParams = new URLSearchParams(nonFilterQquery);
    urlSearchParams.set('setting', 'Virtual');
    urlSearchParams.set('address', `${redirectState?.label}, USA`);
    if (effectiveTypeOfCare) {
      urlSearchParams.set('typeOfCare', effectiveTypeOfCare);
    }

    const params = decodeURIComponent(urlSearchParams.toString());
    updatedUrl += `?${params}`;

    setBaseRedirectUrl(updatedUrl);
  }, [effectiveTypeOfCare, selectedItems.state, selectedItems.insurance, nonFilterQquery])

  // Listen for sync events from other filter instances
  useEffect(() => {
    const handleStateSelected = (event: CustomEvent) => {
      const syncedState = event.detail;
      if (syncedState !== selectedItems.state) {
        setSelectedItems(prev => ({ ...prev, state: syncedState }));
        // Clear the state error for both selection and deselection
        setErrors(prev => ({ ...prev, state: "" }));
      }
    };

    const handleInsuranceSelected = (event: CustomEvent) => {
      const syncedInsurance = event.detail;
      if (syncedInsurance !== selectedItems.insurance) {
        setSelectedItems(prev => ({ ...prev, insurance: syncedInsurance }));
        // Clear the insurance error for both selection and deselection
        setErrors(prev => ({ ...prev, insurance: "" }));
      }
    };

    const handleNeedsSelected = (event: CustomEvent) => {
      const syncedSpecialty = event.detail;
      if (syncedSpecialty !== selectedItems.specialty) {
        setSelectedItems(prev => ({ ...prev, specialty: syncedSpecialty }));
        // If needs are deselected (null), set selectLater to true
        // If needs are selected, set selectLater to false
        setSelectLater(prev => ({ ...prev, selected: !syncedSpecialty }));
      }
    };

    const handleTypeOfCareChanged = (event: CustomEvent) => {
      const syncedTypeOfCare = event.detail;
      if (syncedTypeOfCare !== effectiveTypeOfCare) {
        setEffectiveTypeOfCare(syncedTypeOfCare);
      }
    };

    const handleValidationUpdated = (event: CustomEvent) => {
      const syncedErrors = event.detail;
      if (syncedErrors) {
        setErrors(syncedErrors);
      }
    };

    // Listen for custom events from useFilterSync
    document.addEventListener('filter-sync-state-selected', handleStateSelected as EventListener);
    document.addEventListener('filter-sync-insurance-selected', handleInsuranceSelected as EventListener);
    document.addEventListener('filter-sync-needs-selected', handleNeedsSelected as EventListener);
    document.addEventListener('filter-sync-type-of-care-changed', handleTypeOfCareChanged as EventListener);
    document.addEventListener('filter-sync-validation-updated', handleValidationUpdated as EventListener);

    return () => {
      document.removeEventListener('filter-sync-state-selected', handleStateSelected as EventListener);
      document.removeEventListener('filter-sync-insurance-selected', handleInsuranceSelected as EventListener);
      document.removeEventListener('filter-sync-needs-selected', handleNeedsSelected as EventListener);
      document.removeEventListener('filter-sync-type-of-care-changed', handleTypeOfCareChanged as EventListener);
      document.removeEventListener('filter-sync-validation-updated', handleValidationUpdated as EventListener);
    };
  }, [selectedItems.state, selectedItems.insurance, selectedItems.specialty, effectiveTypeOfCare]);

  const requiredFiltersAllValid = () => {
    let query = setQuery(selectedItems);
    if (selectLater.selected) {
      query = `${query}&specialty[0]=${selectLater.label}`;
    }

    return !setDisabled();
  }

  const getRedirectUrl = () => {
    let query = setQuery(selectedItems);
    if (selectLater.selected) {
      query = `${query}&specialty[0]=${selectLater.label}`;
    }

    if (!setDisabled()) {
      return encodeURI(
        `${BaseRedirectUrl.replace(
          "{origin}",
          import.meta.env.PUBLIC_FILTER_API_URL,
        )}${query.replace("?", "&")}`,
      );
    }
    return "";
  };

  const handleRedirectClick = () => {
    let query = setQuery(selectedItems);
    if (selectLater.selected) {
      query = `${query}&specialty[0]=${selectLater.label}`;
    }

    if (!setDisabled()) {
      let history = createBrowserHistory();
      const currentUrl = `${window.location.origin}${window.location.pathname}${query}${nonFilterQquery}`;
      history.push(encodeURI(currentUrl));

      const redirectUrl = getRedirectUrl();
      onRedirectUrlChange?.(redirectUrl);
      
      setTimeout(() => {
        navigate(redirectUrl);
      }, 0);
    }
  };

  return (
    <ComboboxModalProvider>
      {children &&
        children({
          selectedItems,
          handleRedirectClick,
          getRedirectUrl,
          requiredFiltersAllValid,
          selectLater,
          handleSelectLater,
          isMobile,
          insurance,
          specialty,
          statesFilters,
          handleOpenStates,
          handleOpenInsurance,
          handleOpenNeeds,
          handleGetSelectedInsurance,
          handleGetSelectedState,
          openInsurance,
          openNeeds,
          openStates,
          handleGetSelectedNeeds,
          disabled_features,
          errors,
          type_of_care: effectiveTypeOfCare,
        })}
    </ComboboxModalProvider>
  );
};
